import apiService from "./apiService";

export const hierarchyService = {
  // ===== PLANTAS =====
  plants: {
    getAll: async () => {
      const response = await apiService.get("/plants");
      return response.data?.plants || response.plants || response;
    },
    getById: (id) => apiService.get(`/plants/${id}`),
    create: (data) => apiService.post("/plants", data),
    update: (id, data) => apiService.put(`/plants/${id}`, data),
    delete: (id) => apiService.delete(`/plants/${id}`),
    getProcesses: (id) => apiService.get(`/plants/${id}/processes`),
    getStats: () => apiService.get("/plants/stats"),
  },

  // ===== PROCESOS =====
  processes: {
    getAll: async () => {
      const response = await apiService.get("/processes");
      return response.data?.processes || response.processes || response;
    },
    getById: (id) => apiService.get(`/processes/${id}`),
    create: (data) => apiService.post("/processes", data),
    update: (id, data) => apiService.put(`/processes/${id}`, data),
    delete: (id) => apiService.delete(`/processes/${id}`),
    getRooms: (id) => apiService.get(`/processes/${id}/rooms`),
    getByPlant: async (plantId) => {
      const response = await apiService.get(`/processes/by-plant/${plantId}`);
      return response.data?.processes || response.processes || response;
    },
  },

  // ===== SALAS =====
  rooms: {
    getAll: async () => {
      const response = await apiService.get("/rooms");
      return response.data?.rooms || response.rooms || response;
    },
    getById: (id) => apiService.get(`/rooms/${id}`),
    create: (data) => apiService.post("/rooms", data),
    update: (id, data) => apiService.put(`/rooms/${id}`, data),
    delete: (id) => apiService.delete(`/rooms/${id}`),
    getMachines: (id) => apiService.get(`/rooms/${id}/machines`),
    getByProcess: async (processId) => {
      const response = await apiService.get(`/rooms/by-process/${processId}`);
      return response.data?.rooms || response.rooms || response;
    },
    getByPlant: async (plantId) => {
      const response = await apiService.get(`/rooms/by-plant/${plantId}`);
      return response.data?.rooms || response.rooms || response;
    },
  },

  // ===== MÁQUINAS =====
  machines: {
    getAll: async () => {
      const response = await apiService.get("/machines");
      return response.data?.machines || response.machines || response;
    },
    getById: (id) => apiService.get(`/machines/${id}`),
    create: (data) => apiService.post("/machines", data),
    update: (id, data) => apiService.put(`/machines/${id}`, data),
    delete: (id) => apiService.delete(`/machines/${id}`),
    getCatalogs: (id) => apiService.get(`/machines/${id}/catalogs`),
    getByRoom: async (roomId) => {
      const response = await apiService.get(`/machines/by-room/${roomId}`);
      return response.data?.machines || response.machines || response;
    },
    getByProcess: async (processId) => {
      const response = await apiService.get(`/machines/by-process/${processId}`);
      return response.data?.machines || response.machines || response;
    },
    getByPlant: async (plantId) => {
      const response = await apiService.get(`/machines/by-plant/${plantId}`);
      return response.data?.machines || response.machines || response;
    },
    search: async (searchTerm) => {
      const response = await apiService.get(`/machines/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data?.machines || response.machines || response;
    },
  },

  // ===== JERARQUÍA COMPLETA =====
  hierarchy: {
    // Obtener jerarquía completa (construida desde plantas con populate)
    getFull: async () => {
      try {
        const response = await apiService.get("/plants");
        const plants = response.data?.plants || response.plants || response;
        return hierarchyService.utils.buildHierarchyTree(plants);
      } catch (error) {
        console.error("Error al obtener jerarquía completa:", error);
        throw error;
      }
    },

    // Obtener jerarquía desde una planta específica
    getFromPlant: async (plantId) => {
      try {
        const plant = await apiService.get(`/plants/${plantId}`);
        return hierarchyService.utils.buildHierarchyTree([plant.data || plant]);
      } catch (error) {
        console.error("Error al obtener jerarquía de planta:", error);
        throw error;
      }
    },

    // Obtener breadcrumb path para una máquina
    getBreadcrumb: async (machineId) => {
      try {
        const machine = await apiService.get(`/machines/${machineId}`);
        const machineData = machine.data || machine;

        // Construir breadcrumb manualmente
        const breadcrumb = [];

        // Obtener sala si existe
        if (machineData.room) {
          const room = await apiService.get(`/rooms/${machineData.room}`);
          const roomData = room.data || room;
          breadcrumb.unshift({
            id: roomData._id,
            name: roomData.name,
            type: "room",
          });

          // Obtener proceso si existe
          if (roomData.process) {
            const process = await apiService.get(`/processes/${roomData.process}`);
            const processData = process.data || process;
            breadcrumb.unshift({
              id: processData._id,
              name: processData.name,
              type: "process",
            });

            // Obtener planta si existe
            if (processData.plant) {
              const plant = await apiService.get(`/plants/${processData.plant}`);
              const plantData = plant.data || plant;
              breadcrumb.unshift({
                id: plantData._id,
                name: plantData.name,
                type: "plant",
              });
            }
          }
        }

        // Agregar la máquina al final
        breadcrumb.push({
          id: machineData._id,
          name: machineData.name,
          type: "machine",
        });

        return breadcrumb;
      } catch (error) {
        console.error("Error al obtener breadcrumb:", error);
        throw error;
      }
    },
  },

  // ===== BÚSQUEDA GLOBAL =====
  search: {
    global: (searchTerm, filters = {}) => {
      const params = new URLSearchParams({
        q: searchTerm,
        ...filters,
      });
      return apiService.get(`/search/global?${params.toString()}`);
    },
  },

  // ===== UTILIDADES =====
  utils: {
    // Construir árbol jerárquico desde datos planos
    buildHierarchyTree: (data) => {
      const tree = [];

      data.forEach((plant) => {
        const plantNode = {
          id: plant._id,
          name: plant.name,
          type: "plant",
          description: plant.description,
          children: [],
        };

        plant.processes?.forEach((process) => {
          const processNode = {
            id: process._id,
            name: process.name,
            type: "process",
            processType: process.processType,
            children: [],
          };

          process.rooms?.forEach((room) => {
            const roomNode = {
              id: room._id,
              name: room.name,
              type: "room",
              roomCode: room.roomCode,
              children: [],
            };

            room.machines?.forEach((machine) => {
              const machineNode = {
                id: machine._id,
                name: machine.name,
                type: "machine",
                machineCode: machine.machineCode,
                operationalStatus: machine.operationalStatus,
                catalogCount: machine.catalogCount || 0,
              };

              roomNode.children.push(machineNode);
            });

            processNode.children.push(roomNode);
          });

          plantNode.children.push(processNode);
        });

        tree.push(plantNode);
      });

      return tree;
    },

    // Encontrar un nodo en el árbol por ID
    findNodeById: (tree, nodeId) => {
      for (const node of tree) {
        if (node.id === nodeId) {
          return node;
        }
        if (node.children) {
          const found = hierarchyService.utils.findNodeById(node.children, nodeId);
          if (found) return found;
        }
      }
      return null;
    },

    // Obtener ruta desde la raíz hasta un nodo
    getPathToNode: (tree, nodeId, path = []) => {
      for (const node of tree) {
        const currentPath = [...path, node];

        if (node.id === nodeId) {
          return currentPath;
        }

        if (node.children) {
          const found = hierarchyService.utils.getPathToNode(node.children, nodeId, currentPath);
          if (found) return found;
        }
      }
      return null;
    },

    // Obtener todos los nodos de un tipo específico
    getNodesByType: (tree, type) => {
      const nodes = [];

      const traverse = (nodes) => {
        nodes.forEach((node) => {
          if (node.type === type) {
            nodes.push(node);
          }
          if (node.children) {
            traverse(node.children);
          }
        });
      };

      traverse(tree);
      return nodes;
    },

    // Contar elementos por tipo
    getHierarchyStats: (tree) => {
      const stats = {
        plants: 0,
        processes: 0,
        rooms: 0,
        machines: 0,
        catalogs: 0,
      };

      const traverse = (nodes) => {
        nodes.forEach((node) => {
          switch (node.type) {
            case "plant":
              stats.plants++;
              break;
            case "process":
              stats.processes++;
              break;
            case "room":
              stats.rooms++;
              break;
            case "machine":
              stats.machines++;
              stats.catalogs += node.catalogCount || 0;
              break;
          }

          if (node.children) {
            traverse(node.children);
          }
        });
      };

      traverse(tree);
      return stats;
    },
  },
};

export default hierarchyService;
